import { useState } from 'react';
import { Plus, MoreHorizontal, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StaffFormModal } from './StaffFormModal';
import { StaffAssignModal } from './StaffAssignModal';
import { useStaff, useStaffMutations } from './useStaff';
import type { StaffClinance, StaffRole } from '@/types/database';

const ROLE_LABELS: Record<StaffRole, string> = {
  admin_clinance: 'Administrador',
  gerente_operacao: 'Gerente de Operações',
  consultor: 'Consultor',
  comercial: 'Comercial',
};

const ROLE_BADGE: Record<StaffRole, string> = {
  admin_clinance: 'bg-purple-100 text-purple-700 border-purple-200',
  gerente_operacao: 'bg-blue-100 text-blue-700 border-blue-200',
  consultor: 'bg-teal-100 text-teal-700 border-teal-200',
  comercial: 'bg-orange-100 text-orange-700 border-orange-200',
};

export function StaffPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffClinance | null>(null);
  const [assignStaff, setAssignStaff] = useState<StaffClinance | null>(null);
  const [deletingStaff, setDeletingStaff] = useState<StaffClinance | null>(null);

  const { data: staffList = [], isLoading } = useStaff();
  const { remove } = useStaffMutations();

  const openCreate = () => {
    setEditingStaff(null);
    setFormOpen(true);
  };
  const openEdit = (s: StaffClinance) => {
    setEditingStaff(s);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditingStaff(null);
  };

  const confirmDelete = async () => {
    if (!deletingStaff) return;
    await remove.mutateAsync(deletingStaff.id);
    setDeletingStaff(null);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Colaboradores</h1>
          <p className="text-sm text-muted-foreground">
            {staffList.length} colaborador{staffList.length !== 1 ? 'es' : ''} cadastrado
            {staffList.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Novo colaborador
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="hidden sm:table-cell">E-mail</TableHead>
              <TableHead>Função</TableHead>
              <TableHead className="hidden md:table-cell">Status</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                  Carregando colaboradores…
                </TableCell>
              </TableRow>
            ) : staffList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                  Nenhum colaborador cadastrado.
                </TableCell>
              </TableRow>
            ) : (
              staffList.map((staff) => (
                <TableRow key={staff.id} className="hover:bg-secondary/40">
                  <TableCell className="font-medium text-foreground">{staff.name}</TableCell>
                  <TableCell className="hidden text-sm text-muted-foreground sm:table-cell">
                    {staff.email}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs font-medium ${ROLE_BADGE[staff.role]}`}
                    >
                      {ROLE_LABELS[staff.role]}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        staff.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {staff.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(staff)}>
                          <Pencil size={14} className="mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setAssignStaff(staff)}>
                          <Users size={14} className="mr-2" />
                          Atribuir clientes
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeletingStaff(staff)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <StaffFormModal open={formOpen} onClose={closeForm} staff={editingStaff} />
      <StaffAssignModal
        open={Boolean(assignStaff)}
        onClose={() => setAssignStaff(null)}
        staff={assignStaff}
      />

      <AlertDialog open={Boolean(deletingStaff)} onOpenChange={(v) => !v && setDeletingStaff(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir colaborador</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{deletingStaff?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
